import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Sidebar = ({ isCollapsed = false, onToggle }) => {
  const location = useLocation();
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const navigationItems = [
    {
      label: 'Dashboard',
      path: '/dashboard',
      icon: 'LayoutDashboard',
      tooltip: 'Financial overview and AI insights'
    },
    {
      label: 'Sales Hub',
      path: '/sales-hub',
      icon: 'TrendingUp',
      tooltip: 'Quotes, invoices, and customer management'
    },
    {
      label: 'Purchase Center',
      path: '/purchase-center',
      icon: 'ShoppingCart',
      tooltip: 'Bills, expenses, and vendor management'
    },
    {
      label: 'Items Management',
      path: '/items-management',
      icon: 'Package',
      tooltip: 'Product and service catalog'
    },
    {
      label: 'Time Tracking',
      path: '/time-tracking',
      icon: 'Clock',
      tooltip: 'Project time and timesheet management'
    },
    {
      label: 'Banking',
      path: '/banking',
      icon: 'CreditCard',
      tooltip: 'Transactions and reconciliation'
    },
    {
      label: 'Compliance',
      path: '/compliance-center',
      icon: 'Shield',
      tooltip: 'VAT returns and regulatory filing'
    },
    {
      label: 'Accountant Tools',
      path: '/accountant-tools',
      icon: 'Calculator',
      tooltip: 'Advanced controls and period-end management'
    },
    {
      label: 'Reports',
      path: '/reports-dashboard',
      icon: 'BarChart3',
      tooltip: 'Financial reporting and analytics'
    },
    {
      label: 'Documents',
      path: '/document-manager',
      icon: 'FileText',
      tooltip: 'Centralized file storage and organization'
    }
  ];

  const isActive = (path) => location?.pathname === path;

  const handleNavigation = (path) => {
    window.location.href = path;
    setIsMobileOpen(false);
  };

  return (
    <>
      {/* Mobile Menu Button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        className="fixed top-4 left-4 z-mobile-nav lg:hidden"
      >
        <Icon name={isMobileOpen ? 'X' : 'Menu'} size={20} />
      </Button>
      {/* Mobile Backdrop */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-mobile-nav lg:hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}
      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 h-full bg-surface border-r border-border shadow-professional z-sidebar
          transition-transform duration-300 ease-in-out
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
          ${isCollapsed ? 'w-16' : 'w-64'}
        `}
      >
        <div className="flex flex-col h-full">
          {/* Logo Section */}
          <div className={`flex items-center ${isCollapsed ? 'justify-center px-4' : 'px-6'} py-6 border-b border-border`}>
            {isCollapsed ? (
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="Calculator" size={20} color="white" />
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                  <Icon name="Calculator" size={20} color="white" />
                </div>
                <span className="text-xl font-semibold text-foreground">AccounTech AI</span>
              </div>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
            {navigationItems?.map((item) => (
              <div key={item?.path} className="relative group">
                <button
                  onClick={() => handleNavigation(item?.path)}
                  className={`
                    w-full flex items-center space-x-3 px-3 py-3 rounded-lg text-sm font-medium
                    transition-all duration-150 ease-in-out
                    ${isActive(item?.path)
                      ? 'bg-primary/10 text-primary border-r-2 border-primary' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }
                    ${isCollapsed ? 'justify-center' : ''}
                  `}
                >
                  <Icon name={item?.icon} size={20} />
                  {!isCollapsed && <span>{item?.label}</span>}
                </button>

                {/* Tooltip for collapsed state */}
                {isCollapsed && (
                  <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-3 py-2 bg-popover border border-border rounded-lg shadow-elevated opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-dropdown">
                    <p className="text-sm font-medium text-foreground">{item?.label}</p>
                    <p className="text-xs text-muted-foreground mt-1">{item?.tooltip}</p>
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Collapse Toggle (Desktop Only) */}
          {onToggle && (
            <div className="hidden lg:block p-4 border-t border-border">
              <Button
                variant="ghost"
                size="sm"
                onClick={onToggle}
                className={`w-full ${isCollapsed ? 'justify-center' : 'justify-start'}`}
              >
                <Icon name={isCollapsed ? 'ChevronRight' : 'ChevronLeft'} size={16} />
                {!isCollapsed && <span className="ml-2">Collapse</span>}
              </Button>
            </div>
          )}

          {/* User Section (Mobile Only) */}
          <div className="lg:hidden p-4 border-t border-border">
            <div className="flex items-center space-x-3 p-3 rounded-lg bg-muted/30">
              <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                <Icon name="User" size={16} color="white" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">John Doe</p>
                <p className="text-xs text-muted-foreground">john@company.com</p>
              </div>
            </div>
          </div>
        </div>
      </aside>
      {/* Main Content Spacer */}
      <div className={`hidden lg:block ${isCollapsed ? 'w-16' : 'w-64'} flex-shrink-0`} />
    </>
  );
};

export default Sidebar;